import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { streamBriefReport } from '@/lib/chart.api';
import {
  InsightCard,
  PremiumReportRenderer,
  ReportEmptyState,
  ReportPaper,
  parseReportSegments,
} from '@/components/premium/ReportPaper';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';

interface ReportPanelProps {
  chartId: string;
  initialContent?: string | null;
  onReportDone?: (reportId?: string, content?: string) => void;
}

type Phase = 'idle' | 'streaming' | 'done' | 'error';

export function ReportPanel({ chartId, initialContent, onReportDone }: ReportPanelProps) {
  const [phase, setPhase] = useState<Phase>(initialContent ? 'done' : 'idle');
  const [content, setContent] = useState(initialContent ?? '');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<{ abort: () => void } | null>(null);
  const contentRef = useRef(content);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    abortRef.current?.abort();
    setContent(initialContent ?? '');
    setPhase(initialContent ? 'done' : 'idle');
    setError(null);
  }, [chartId, initialContent]);

  const start = () => {
    setPhase('streaming');
    setContent('');
    contentRef.current = '';
    setError(null);
    abortRef.current = streamBriefReport(chartId, {
      onChunk: (delta) => {
        contentRef.current += delta;
        setContent(contentRef.current);
      },
      onDone: (reportId) => {
        setPhase('done');
        onReportDone?.(reportId, contentRef.current);
      },
      onError: (msg) => {
        setError(msg);
        setPhase('error');
      },
    });
  };

  const segments = parseReportSegments(content);

  return (
    <ReportPaper
      title="观微先生 · 简版报告"
      subtitle="快速生成格局定调与天赋性格摘要，作为完整解读前的命盘基准。"
      progress={
        phase === 'streaming'
          ? Math.min(90, Math.max(12, content.length / 8))
          : phase === 'done'
            ? 100
            : undefined
      }
      action={
        phase === 'idle' ? (
          <Button onClick={start}>{content ? '重新生成' : '开始生成'}</Button>
        ) : phase === 'streaming' ? (
          <Button variant="outline" onClick={() => abortRef.current?.abort()}>
            停止
          </Button>
        ) : (
          <Button variant="outline" onClick={start}>
            重新生成
          </Button>
        )
      }
    >
      {phase === 'idle' && (
        <ReportEmptyState>
          <p className="font-serif text-lg text-fg mb-2">等待生成</p>
          <p>
            点击右上角「开始生成」，观微先生会先给出两段基准判断。报告生成过程中会逐步点亮章节。
          </p>
        </ReportEmptyState>
      )}

      {phase === 'error' && (
        <InsightCard title="报告生成暂时失败">
          <p>请稍后重试。若多次失败，请联系客服（错误码：GEN_FAIL）。</p>
          {error && <p className="mt-2 text-fg-muted">{error}</p>}
        </InsightCard>
      )}

      {phase === 'streaming' && !content && <AnalyzingOverlay />}

      {((phase === 'streaming' && content) || phase === 'done') && (
        <PremiumReportRenderer segments={segments} streaming={phase === 'streaming'} />
      )}
    </ReportPaper>
  );
}
